import { SiteHeader } from "@/components/site-header"
import { GitMergeIcon, HomeIcon, ScissorsIcon, ArrowRightIcon, AlertTriangleIcon, XCircleIcon } from "lucide-react"
import { cn } from "@/lib/utils"

function SectionHeader({ icon, title }: { icon: React.ReactNode; title: string }) {
  return (
    <div className="flex items-center gap-2.5 mb-4">
      <span className="text-muted-foreground">{icon}</span>
      <h2 className="text-base font-semibold">{title}</h2>
    </div>
  )
}

function Step({ n, title, desc }: { n: string; title: string; desc: string }) {
  return (
    <li className="flex gap-3">
      <span className="size-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-semibold shrink-0 mt-0.5">
        {n}
      </span>
      <div>
        <p className="text-sm font-medium">{title}</p>
        <p className="text-sm text-muted-foreground">{desc}</p>
      </div>
    </li>
  )
}

function BlockBadge({ label, type }: { label: string; type: "error" | "warning" }) {
  return (
    <div className={cn(
      "flex items-start gap-2 rounded-md border px-3 py-2 text-xs",
      type === "error"
        ? "border-red-200 bg-red-50 text-red-800"
        : "border-yellow-200 bg-yellow-50 text-yellow-800",
    )}>
      {type === "error"
        ? <XCircleIcon className="size-3.5 shrink-0 mt-0.5" />
        : <AlertTriangleIcon className="size-3.5 shrink-0 mt-0.5" />}
      {label}
    </div>
  )
}

function InfoGrid({ rows }: { rows: [string, React.ReactNode][] }) {
  return (
    <div className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-1.5 text-sm">
      {rows.map(([label, value], i) => (
        <>
          <div key={`l${i}`} className="text-muted-foreground whitespace-nowrap">{label}</div>
          <div key={`v${i}`}>{value}</div>
        </>
      ))}
    </div>
  )
}

export default function TripActionsPage() {
  return (
    <>
      <SiteHeader title="Acciones sobre viajes" />
      <div className="flex flex-1 flex-col px-4 py-6 lg:px-6">
        <div className="max-w-5xl space-y-14">

          {/* ── Intro ── */}
          <section>
            <p className="text-sm text-muted-foreground leading-relaxed">
              La pantalla de <strong>Viajes</strong> incluye tres acciones especiales que crean nuevos viajes
              a partir de uno o dos existentes: <strong>Interconexión</strong>, <strong>Volver a base</strong>{" "}
              y <strong>Dividir viaje</strong>. Todas siguen el mismo patrón: un botón que abre un popup de
              preview antes de confirmar la creación.
            </p>
          </section>

          {/* ═══════════════════════════════════════════════════════ */}
          {/* INTERCONEXIÓN                                           */}
          {/* ═══════════════════════════════════════════════════════ */}
          <section className="space-y-6">
            <SectionHeader icon={<GitMergeIcon className="size-5" />} title="Interconexión" />

            <p className="text-sm text-muted-foreground leading-relaxed">
              Crea un viaje de conexión entre el destino de un primer viaje y el origen de un segundo,
              cuando el mismo chofer tiene dos viajes separados que necesitan un tramo de unión.
            </p>

            {/* Cómo activarlo */}
            <div>
              <h3 className="text-sm font-semibold mb-3">Cómo activarlo</h3>
              <ul className="space-y-2 text-sm text-muted-foreground list-disc list-inside">
                <li>Seleccionar exactamente <strong>2 viajes con el mismo chofer</strong> usando los checkboxes de la tabla.</li>
                <li>El botón <strong>Interconexión</strong> se habilita en la barra de selección.</li>
              </ul>
            </div>

            {/* Reglas de bloqueo */}
            <div>
              <h3 className="text-sm font-semibold mb-3">Condiciones de bloqueo</h3>
              <p className="text-xs text-muted-foreground mb-2">
                El botón siempre es clickeable cuando hay 2 viajes del mismo chofer seleccionados.
                Si alguna condición no se cumple, el motivo aparece como banner rojo dentro del popup
                y el botón "Crear viaje" se oculta.
              </p>
              <div className="space-y-2">
                <BlockBadge type="error" label="Alguno de los viajes no tiene fecha de salida registrada." />
                <BlockBadge type="error" label="El destino del primer viaje y el origen del segundo son la misma ciudad." />
              </div>
            </div>

            {/* Viaje creado */}
            <div>
              <h3 className="text-sm font-semibold mb-3">Viaje creado</h3>
              <div className="rounded-lg border bg-muted/40 p-4 space-y-3">
                <div className="flex items-center gap-2 text-sm flex-wrap">
                  <span className="text-muted-foreground font-medium">Origen:</span>
                  <span>Destino del viaje más temprano</span>
                  <ArrowRightIcon className="size-3 shrink-0 text-muted-foreground" />
                  <span className="text-muted-foreground font-medium">Destino:</span>
                  <span>Origen del viaje más tardío</span>
                </div>
                <InfoGrid rows={[
                  ["Chofer / Camión / Semi", "Copiados del viaje más tardío"],
                  ["Fecha de salida", "Punto medio entre los departureAt de ambos viajes"],
                  ["Fecha de llegada", "Igual a la fecha de salida"],
                  ["Estado", <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium bg-emerald-100 text-emerald-700">Completado</span>],
                  ["Carga", "Sin carga asociada"],
                  ["KM", "Autocompletado si existe ruta entre las ciudades"],
                ]} />
              </div>
            </div>

            {/* Advertencia KM */}
            <BlockBadge
              type="warning"
              label="Si no existe una ruta configurada entre las dos ciudades, el campo KM quedará vacío. Se puede editar el viaje creado para completarlo manualmente."
            />
          </section>

          <hr className="border-border" />

          {/* ═══════════════════════════════════════════════════════ */}
          {/* VOLVER A BASE                                           */}
          {/* ═══════════════════════════════════════════════════════ */}
          <section className="space-y-6">
            <SectionHeader icon={<HomeIcon className="size-5" />} title="Volver a base" />

            <p className="text-sm text-muted-foreground leading-relaxed">
              Crea un viaje de regreso desde el destino del viaje seleccionado hasta la ciudad base
              configurada (Chivilcoy). Útil para registrar el retorno del chofer después de completar
              una entrega.
            </p>

            {/* Cómo activarlo */}
            <div>
              <h3 className="text-sm font-semibold mb-3">Cómo activarlo</h3>
              <ul className="space-y-2 text-sm text-muted-foreground list-disc list-inside">
                <li>Seleccionar <strong>exactamente 1 viaje</strong> usando el checkbox de la tabla.</li>
                <li>El botón <strong>Volver a base</strong> se habilita en la barra de selección.</li>
                <li>También disponible en el menú de 3 puntos (⋮) de cada fila.</li>
              </ul>
            </div>

            {/* Reglas de bloqueo */}
            <div>
              <h3 className="text-sm font-semibold mb-3">Condiciones de bloqueo</h3>
              <p className="text-xs text-muted-foreground mb-2">
                Al igual que Interconexión, el botón abre el popup siempre. Los bloqueos se muestran
                dentro del popup como banner rojo.
              </p>
              <div className="space-y-2">
                <BlockBadge type="error" label="La ciudad base no está configurada en app.config.json (trips.basePartyId)." />
                <BlockBadge type="error" label="El viaje no tiene ciudad de destino configurada." />
                <BlockBadge type="error" label="El viaje no tiene fecha de salida ni de llegada — no se pueden calcular las fechas del regreso." />
                <BlockBadge type="error" label="El destino del viaje ya es la base. No es necesario crear un viaje de regreso." />
              </div>
            </div>

            {/* Viaje creado */}
            <div>
              <h3 className="text-sm font-semibold mb-3">Viaje creado</h3>
              <div className="rounded-lg border bg-muted/40 p-4 space-y-3">
                <div className="flex items-center gap-2 text-sm flex-wrap">
                  <span className="text-muted-foreground font-medium">Origen:</span>
                  <span>Destino del viaje seleccionado</span>
                  <ArrowRightIcon className="size-3 shrink-0 text-muted-foreground" />
                  <span className="text-muted-foreground font-medium">Destino:</span>
                  <span>Chivilcoy (ciudad base)</span>
                </div>
                <InfoGrid rows={[
                  ["Chofer / Camión / Semi", "Copiados del viaje seleccionado"],
                  ["Fecha de salida", "arrivalAt del viaje origen (si existe), sino departureAt + 8 h"],
                  ["Fecha de llegada", "Fecha de salida + tiempo estimado (KM ÷ 80 km/h), sino + 8 h"],
                  ["Estado", <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium bg-emerald-100 text-emerald-700">Completado</span>],
                  ["Carga", "Sin carga asociada"],
                  ["KM", "Autocompletado si existe ruta entre las ciudades"],
                ]} />
              </div>
            </div>

            {/* Configuración */}
            <div className="rounded-lg border bg-muted/40 p-4 space-y-2">
              <p className="text-xs font-semibold">Configuración — <code className="font-mono">apps/api/app.config.json</code></p>
              <pre className="text-xs font-mono bg-background rounded p-3 border overflow-x-auto">{`{
  "trips": {
    "basePartyId": "<uuid de la parte Base Chiviltoy>",
    "averageSpeedKmh": 80
  }
}`}</pre>
              <p className="text-xs text-muted-foreground">
                <code className="font-mono">basePartyId</code> es el UUID de la entrada en la tabla <strong>Partes</strong> que representa la base.
                La ciudad se resuelve automáticamente desde la parte. <code className="font-mono">averageSpeedKmh</code> se usa
                para estimar la fecha de llegada cuando hay KM de ruta.
              </p>
            </div>

            <BlockBadge
              type="warning"
              label="Si no existe una ruta configurada entre el origen y Chivilcoy, el campo KM quedará vacío y la llegada estimada se calcula sumando 8 h a la salida."
            />
          </section>

          <hr className="border-border" />

          {/* ═══════════════════════════════════════════════════════ */}
          {/* DIVIDIR VIAJE                                           */}
          {/* ═══════════════════════════════════════════════════════ */}
          <section className="space-y-6">
            <SectionHeader icon={<ScissorsIcon className="size-5" />} title="Dividir viaje" />

            <p className="text-sm text-muted-foreground leading-relaxed">
              Parte un viaje existente en dos tramos cuando ocurre un evento en ruta: rotura de camión,
              cambio de chofer, relevo de vehículo, etc. El viaje original conserva su origen y toma como
              nuevo destino la <strong>ciudad de corte</strong>. Un segundo viaje se crea desde esa ciudad
              hacia el destino original.
            </p>

            {/* Cómo activarlo */}
            <div>
              <h3 className="text-sm font-semibold mb-3">Cómo activarlo</h3>
              <ul className="space-y-2 text-sm text-muted-foreground list-disc list-inside">
                <li>En el detalle de una carga, en la sección <strong>Viajes</strong>, en modo edición.</li>
                <li>Hacer clic en el ícono de tijera (✂) del menú de acciones de la fila.</li>
                <li>Requiere permiso de escritura en Envíos.</li>
              </ul>
            </div>

            {/* Pasos */}
            <div>
              <h3 className="text-sm font-semibold mb-3">Flujo</h3>
              <ol className="space-y-3">
                {[
                  ["1", "Elegir ciudad de corte", "Nuevo destino del Tramo 1 y origen del Tramo 2."],
                  ["2", "Seleccionar móvil para el Tramo 2", "Puede ser el mismo u otro. Los recursos (chofer, camión, semi) se rellenan automáticamente."],
                  ["3", "Ajustar recursos del Tramo 2", "Se pueden cambiar chofer, camión y semi de forma independiente sin modificar el móvil en la base de datos."],
                  ["4", "Confirmar", "El viaje original recibe PATCH con el nuevo destinationCityId. Se crea el Tramo 2 con POST /api/trips."],
                ].map(([n, title, desc]) => (
                  <Step key={n} n={n!} title={title!} desc={desc!} />
                ))}
              </ol>
            </div>

            {/* 4 casos */}
            <div>
              <h3 className="text-sm font-semibold mb-3">Casos de uso</h3>
              <div className="grid gap-3 sm:grid-cols-2">
                {[
                  { caso: "Rotura de camión — mismo chofer", desc: "Llega un camión nuevo. Mismo chofer y semi. Se cambia solo el Camión." },
                  { caso: "Cambio de chofer — mismo camión", desc: "El camión sigue. Solo cambia el chofer (y eventualmente el semi)." },
                  { caso: "Relevo total — móvil diferente", desc: "Cambia todo: móvil, chofer, camión y semi. Se selecciona un móvil diferente." },
                  { caso: "Cambio de trailer", desc: "Mismo camión y chofer, distinto semi. Se ajusta solo el Semi en el panel." },
                ].map(({ caso, desc }) => (
                  <div key={caso} className="rounded-lg border bg-muted/40 p-3 space-y-1">
                    <p className="text-xs font-semibold">{caso}</p>
                    <p className="text-xs text-muted-foreground">{desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

        </div>
      </div>
    </>
  )
}
